import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { filter } from 'rxjs/operators';

import { Alert, AlertIcon, AlertType, AlertTitle } from '@core/models';

@Injectable({ providedIn: 'root' })
export class AlertService {
  private defaultId = 'default-alert';
  private alertSubscription = new Subject<Alert>();
  readonly alert$ = this.alertSubscription.asObservable();

  private clearAlertsSubscription = new Subject<Alert[]>();
  readonly clearAlert$ = this.clearAlertsSubscription.asObservable();

  //? enable subscribing to alerts observable
  onAlert(id = this.defaultId): Observable<Alert> {
    return this.alert$.pipe(filter(currAlert => currAlert && currAlert.id === id));
  }

  //? Create Alert
  createAlert(alert: Alert) {
    alert.id = alert.id || this.defaultId;
    alert.uniqueId = alert.uniqueId || Math.random();
    this.alertSubscription.next(alert);
  }

  //? Clear Alerts
  clear(id = this.defaultId) {
    this.alertSubscription.next(new Alert({ id }));
  }

  //? Info Alert Type
  info(message: string) {
    this.createAlert(
      new Alert({ title: AlertTitle.Info, type: AlertType.Info, message, icon: AlertIcon.Info })
    );
  }

  //? Error Alert Type
  error(message: string) {
    this.createAlert(
      new Alert({ title: AlertTitle.Error, type: AlertType.Error, message, icon: AlertIcon.Error })
    );
  }

  //? Banner Alert Type
  banner(message: string) {
    this.createAlert(
      new Alert({
        title: AlertTitle.Banner,
        type: AlertType.Success,
        message,
        icon: AlertIcon.Banner,
      })
    );
  }

  //? Success Alert Type
  success(message: string) {
    this.createAlert(
      new Alert({
        title: AlertTitle.Success,
        type: AlertType.Success,
        message,
        icon: AlertIcon.Success,
      })
    );
  }

  //? Warning Alert Type
  warn(message: string) {
    this.createAlert(
      new Alert({
        title: AlertTitle.Warning,
        type: AlertType.Warning,
        message,
        icon: AlertIcon.Warning,
      })
    );
  }

  showEditingDisabledBanner() {
    this.createAlert(
      new Alert({
        icon: AlertIcon.Warning,
        message: 'Please contact your employer to make any updates to your profile.',
        title: AlertTitle.EditProfile,
        type: AlertType.Warning,
        autoDisappear: false,
        uniqueId: 9999,
      })
    );
  }

  clearAllAlerts() {
    this.clearAlertsSubscription.next([]);
  }
}
